import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, ArrowRight, ShieldCheck, TrendingUp, Coins, Plane,
  LineChart as LineIcon, Brain, Lock, BarChart3, Globe2, Check, Quote,
} from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, Area, AreaChart,
  Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import ParticleBackground from "@/components/site/ParticleBackground";
import Counter from "@/components/site/Counter";
import LeadFormSticky from "@/components/site/LeadFormSticky";
import CalendlyButton from "@/components/site/CalendlyButton";
import WorldMap from "@/components/site/WorldMap";
import BullMascot from "@/components/site/BullMascot";
import FloatingHeroCards from "@/components/site/FloatingHeroCards";
import ReleaseNotesPopup from "@/components/site/ReleaseNotesPopup";
import { trackEvent } from "@/lib/analytics";
import api, { API_BASE } from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/15693280/pexels-photo-15693280.jpeg";

const tickerItems = [
  { sym: "EUR/USD", val: "1.0892", chg: "+0.34%", up: true },
  { sym: "GBP/USD", val: "1.2671", chg: "-0.12%", up: false },
  { sym: "USD/JPY", val: "151.23", chg: "+0.08%", up: true },
  { sym: "XAU/USD", val: "2,378.40", chg: "+1.04%", up: true },
  { sym: "BTC/USD", val: "67,420", chg: "+2.18%", up: true },
  { sym: "S&P 500", val: "5,234.18", chg: "+0.46%", up: true },
  { sym: "NIFTY", val: "22,178", chg: "+0.22%", up: true },
  { sym: "USD/INR", val: "83.41", chg: "-0.05%", up: false },
];

const chartData = [
  { m: "Jan", v: 100 }, { m: "Feb", v: 112 }, { m: "Mar", v: 108 },
  { m: "Apr", v: 124 }, { m: "May", v: 138 }, { m: "Jun", v: 152 },
  { m: "Jul", v: 148 }, { m: "Aug", v: 168 }, { m: "Sep", v: 182 },
  { m: "Oct", v: 198 }, { m: "Nov", v: 210 }, { m: "Dec", v: 234 },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function HomePage() {
  const [testimonials, setTestimonials] = React.useState([]);
  const [faqs, setFaqs] = React.useState([]);
  const [blog, setBlog] = React.useState([]);

  React.useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
    api.get("/blog").then((r) => setBlog(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <ReleaseNotesPopup />
      <LeadFormSticky source="home" />
      {/* ---------------- HERO ---------------- */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden grain pt-24 lg:pt-0">
        {/* Deep gradient backdrop */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(14,165,233,0.22),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(212,175,55,0.14),transparent_55%),linear-gradient(180deg,#020617_0%,#030712_60%,#030712_100%)]" />

        {/* Animated world map */}
        <div className="absolute inset-0 opacity-[0.55]">
          <WorldMap />
        </div>

        {/* Particle field */}
        <ParticleBackground density={70} />

        {/* Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,#030712_95%)] pointer-events-none" />

        <div className="mfb-container relative z-10 pb-28 lg:pb-24">
          <div className="grid lg:grid-cols-12 gap-8 items-center min-h-[80vh]">
            {/* LEFT — copy + CTAs */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.13 } } }}
              className="lg:col-span-6"
            >
              <motion.div
                variants={fadeUp}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 backdrop-blur px-3 py-1.5 mb-6"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="text-xs tracking-widest uppercase text-slate-200">AI-driven Wealth Engine · v4.0</span>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="font-[Outfit] text-5xl sm:text-6xl lg:text-[5.5rem] font-light tracking-tighter text-white leading-[0.98] text-balance"
                data-testid="hero-headline"
              >
                <span className="block">Where</span>
                <span className="block">
                  <span className="gradient-text-sky font-medium">Wealth</span>{" "}
                  <span className="text-slate-400 font-light">Meets</span>
                </span>
                <span className="block">
                  <span className="gradient-text-gold font-medium">Intelligence</span>
                  <span className="text-[#D4AF37]">.</span>
                </span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-7 max-w-xl text-base sm:text-lg text-slate-300/90 leading-relaxed"
              >
                India&rsquo;s premium financial super-app for the next generation of investors.
                Invest, trade forex, buy gold, book travel and grow your wealth with AI.
              </motion.p>

              <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3 sm:gap-4">
                <Link
                  to="/contact"
                  className="btn-primary text-sm"
                  data-testid="hero-open-account-btn"
                  onClick={() => trackEvent("open_account_click", { source: "hero", cta: "Open Web App" })}
                >
                  Open Web App <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={`${API_BASE}/download/latest`}
                  className="btn-gold text-sm"
                  data-testid="hero-download-app-btn"
                  onClick={() => trackEvent("download_app_click", { source: "hero", platform: "android" })}
                >
                  Download Android APK
                </a>
                <CalendlyButton
                  label="Contact Advisor"
                  source="hero"
                  className="btn-ghost text-sm"
                  testId="hero-contact-advisor-btn"
                />
              </motion.div>

              <motion.div
                variants={fadeUp}
                className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-2xl"
              >
                {[
                  { v: 12.4, suffix: "B+", label: "Assets Under Mgmt", prefix: "$" },
                  { v: 128, suffix: "K+", label: "Active Clients" },
                  { v: 22.6, suffix: "%", label: "Avg. 5yr Return" },
                  { v: 41, suffix: "+", label: "Countries" },
                ].map((s, i) => (
                  <div key={i} className="glass rounded-2xl p-3 sm:p-4">
                    <div className="font-[Outfit] text-xl sm:text-2xl text-white font-light tracking-tight">
                      <Counter to={s.v} prefix={s.prefix || ""} suffix={s.suffix} />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{s.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* RIGHT — Bull mascot + floating cards */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, ease: "easeOut" }}
              className="lg:col-span-6 relative flex items-center justify-center min-h-[480px] lg:min-h-[640px]"
            >
              {/* Concentric rotating rings */}
              <motion.div
                className="absolute w-[520px] h-[520px] rounded-full border border-sky-500/15"
                animate={{ rotate: 360 }}
                transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
              />
              <motion.div
                className="absolute w-[420px] h-[420px] rounded-full border border-[#D4AF37]/15"
                animate={{ rotate: -360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
              />
              <motion.div
                className="absolute w-[600px] h-[600px] rounded-full border border-white/[0.04]"
                animate={{ rotate: 360 }}
                transition={{ duration: 120, repeat: Infinity, ease: "linear" }}
              />

              {/* Bull mascot */}
              <BullMascot size={440} />

              {/* Floating cards (desktop) */}
              <FloatingHeroCards />
            </motion.div>
          </div>

          {/* Mobile floating cards (horizontal scroll) */}
          <FloatingHeroCards inlineForMobile />
        </div>

        {/* ticker tape */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/5 bg-[#030712]/90 backdrop-blur z-10 overflow-hidden">
          <div className="animate-ticker flex gap-10 whitespace-nowrap py-3 px-4 text-xs font-mono" data-testid="market-ticker">
            {[...tickerItems, ...tickerItems].map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-slate-400">{t.sym}</span>
                <span className="text-white">{t.val}</span>
                <span className={t.up ? "text-emerald-400" : "text-rose-400"}>{t.chg}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- PRODUCTS BENTO ---------------- */}
      <section className="mfb-section" data-testid="products-section">
        <div className="mfb-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="overline mb-3">Wealth Suite</div>
              <h2 className="font-[Outfit] text-4xl sm:text-5xl font-light tracking-tight text-white max-w-2xl">
                Five products. <span className="gradient-text-gold">One institutional-grade</span> platform.
              </h2>
            </div>
            <p className="text-slate-400 max-w-md text-sm leading-relaxed">
              From AI-curated equity baskets to physical gold delivery — every asset class your portfolio needs, engineered for the next decade.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {/* Investments - large */}
            <Link to="/investments" data-testid="bento-investments" className="card-luxury md:col-span-7 p-8 sm:p-10 min-h-[420px] flex flex-col justify-between group">
              <div>
                <div className="flex items-center gap-2 mb-5">
                  <TrendingUp className="w-5 h-5 text-sky-400" />
                  <span className="overline text-sky-400">Investments</span>
                </div>
                <h3 className="font-[Outfit] text-3xl sm:text-4xl font-light text-white tracking-tight">Mutual Funds, SIPs, Bonds &amp; Stocks</h3>
                <p className="mt-4 text-slate-400 text-sm max-w-md leading-relaxed">
                  AI-curated baskets across 4,000+ instruments. Zero account opening, ultra-low fees, automated SIPs.
                </p>
              </div>
              <div className="mt-6">
                <ResponsiveContainer width="100%" height={140}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="v" stroke="#0EA5E9" strokeWidth={2} fill="url(#g1)" />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="flex items-center justify-between mt-3">
                  <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/15">+22.6% avg 5yr return</Badge>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white group-hover:translate-x-1 transition" />
                </div>
              </div>
            </Link>

            {/* Gold */}
            <Link to="/gold" data-testid="bento-gold" className="card-luxury md:col-span-5 p-8 min-h-[420px] flex flex-col justify-between group relative">
              <img
                src="https://images.pexels.com/photos/321452/pexels-photo-321452.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Gold"
                className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 transition-opacity"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#0B1221]/40 to-[#0B1221]" />
              <div className="relative z-10">
                <Coins className="w-6 h-6 text-[#D4AF37]" />
                <h3 className="mt-4 font-[Outfit] text-3xl font-light text-white tracking-tight">Digital Gold &amp; SGBs</h3>
                <p className="mt-3 text-slate-400 text-sm max-w-md leading-relaxed">
                  24K certified gold from $10. Redeem in cash or take physical delivery, anywhere in the world.
                </p>
              </div>
              <div className="relative z-10 flex items-center gap-2 text-[#D4AF37] text-sm">
                Explore Gold <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            {/* Forex */}
            <Link to="/forex" data-testid="bento-forex" className="card-luxury md:col-span-5 p-8 min-h-[360px] flex flex-col justify-between group">
              <div>
                <div className="flex items-center gap-2">
                  <LineIcon className="w-5 h-5 text-emerald-400" />
                  <span className="overline text-emerald-400">Forex</span>
                </div>
                <h3 className="mt-4 font-[Outfit] text-3xl font-light text-white tracking-tight">Trade 60+ currency pairs</h3>
                <p className="mt-3 text-slate-400 text-sm leading-relaxed">
                  Institutional spreads from 0.0 pips. 24/5 markets, smart risk controls, deep liquidity.
                </p>
              </div>
              <ResponsiveContainer width="100%" height={80}>
                <LineChart data={chartData}>
                  <Line type="monotone" dataKey="v" stroke="#10B981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Link>

            {/* Travel */}
            <Link to="/travel" data-testid="bento-travel" className="card-luxury md:col-span-7 p-8 min-h-[360px] flex flex-col justify-between group relative overflow-hidden">
              <img
                src="https://images.pexels.com/photos/35026952/pexels-photo-35026952.jpeg"
                alt="Travel"
                className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 transition-opacity"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0B1221] via-[#0B1221]/60 to-transparent" />
              <div className="relative z-10 max-w-md">
                <Plane className="w-6 h-6 text-sky-400" />
                <h3 className="mt-4 font-[Outfit] text-3xl font-light text-white tracking-tight">Luxury Travel, redefined</h3>
                <p className="mt-3 text-slate-400 text-sm leading-relaxed">
                  Metal multi-currency travel cards · airport lounge access · concierge bookings across 800+ destinations.
                </p>
              </div>
              <div className="relative z-10 flex items-center gap-2 text-sky-400 text-sm">
                Discover Travel <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------- AI ENGINE ---------------- */}
      <section className="mfb-section relative overflow-hidden" data-testid="ai-engine-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(14,165,233,0.12),transparent_60%)]" />
        <div className="mfb-container relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="overline mb-4">The MFB AI Engine</div>
              <h2 className="font-[Outfit] text-4xl sm:text-5xl font-light tracking-tight text-white">
                Markets move in milliseconds.
                <span className="block gradient-text-sky font-medium">So does our intelligence.</span>
              </h2>
              <p className="mt-6 text-slate-400 leading-relaxed">
                Our proprietary AI engine ingests 2.4M market signals per second across equities, bonds, commodities and FX —
                surfacing actionable, risk-adjusted opportunities to your portfolio in real time.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  { icon: Brain, t: "Predictive Portfolio Engine", d: "Multi-horizon forecasts on 12,000+ instruments." },
                  { icon: Lock, t: "Bank-Grade Security", d: "256-bit encryption, MFA, segregated custody." },
                  { icon: BarChart3, t: "Real-time Analytics", d: "Live risk, P&L, and exposure dashboards." },
                  { icon: Globe2, t: "Global Coverage", d: "Trade across 41 countries from one account." },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center shrink-0 group-hover:border-sky-500/50 transition">
                      <f.icon className="w-4 h-4 text-sky-400" />
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">{f.t}</div>
                      <div className="text-slate-400 text-sm mt-0.5">{f.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="glass rounded-3xl p-6 sm:p-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="overline">Portfolio · Aggressive</div>
                    <div className="font-[Outfit] text-3xl font-light text-white mt-2">$1,248,302.47</div>
                  </div>
                  <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/15">+22.6% YTD</Badge>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="m" stroke="#64748B" fontSize={10} />
                    <YAxis stroke="#64748B" fontSize={10} />
                    <Tooltip contentStyle={{ background: "#0B1221", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                    <Area type="monotone" dataKey="v" stroke="#0EA5E9" strokeWidth={2.5} fill="url(#g2)" />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-3 gap-3 mt-5">
                  {[
                    { l: "Equities", v: "58%", c: "bg-sky-400" },
                    { l: "Gold", v: "18%", c: "bg-[#D4AF37]" },
                    { l: "Bonds", v: "24%", c: "bg-emerald-400" },
                  ].map((a, i) => (
                    <div key={i} className="rounded-xl bg-white/5 p-3 border border-white/5">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${a.c}`} />
                        <div className="text-[10px] uppercase tracking-widest text-slate-400">{a.l}</div>
                      </div>
                      <div className="text-white font-[Outfit] text-xl mt-1">{a.v}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -top-6 -right-4 w-20 h-20 rounded-full bg-sky-500/30 blur-3xl" />
              <div className="absolute -bottom-6 -left-4 w-32 h-32 rounded-full bg-[#D4AF37]/20 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section className="mfb-section border-t border-white/5" data-testid="testimonials-section">
        <div className="mfb-container">
          <div className="overline mb-3">Voices of conviction</div>
          <h2 className="font-[Outfit] text-4xl sm:text-5xl font-light tracking-tight text-white max-w-2xl">
            Trusted by founders, funds <span className="gradient-text-gold">and family offices</span>.
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {testimonials.slice(0, 3).map((t, i) => (
              <div key={t.id} className="card-luxury p-7" data-testid={`testimonial-${i}`}>
                <Quote className="w-6 h-6 text-[#D4AF37] mb-4" />
                <p className="text-slate-200 leading-relaxed text-sm">{t.quote}</p>
                <div className="mt-6 flex items-center gap-3">
                  {t.avatar ? (
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover ring-1 ring-white/10" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-sky-500/20 ring-1 ring-white/10" />
                  )}
                  <div>
                    <div className="text-white text-sm font-medium">{t.name}</div>
                    <div className="text-slate-500 text-xs">{t.role}{t.company ? ` · ${t.company}` : ""}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- BLOG PREVIEW ---------------- */}
      <section className="mfb-section" data-testid="blog-preview-section">
        <div className="mfb-container">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline mb-3">Insights</div>
              <h2 className="font-[Outfit] text-4xl font-light tracking-tight text-white">From the desk of MFB Research</h2>
            </div>
            <Link to="/blog" className="text-sm text-sky-400 hover:text-sky-300 flex items-center gap-1">
              All articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {blog.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="card-luxury overflow-hidden group" data-testid={`blog-card-${p.slug}`}>
                {p.cover_image && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                )}
                <div className="p-6">
                  <Badge className="bg-sky-500/10 text-sky-300 border-sky-500/20 hover:bg-sky-500/15">{p.category}</Badge>
                  <h3 className="mt-3 text-white font-[Outfit] text-lg leading-snug">{p.title}</h3>
                  <p className="mt-2 text-slate-400 text-sm leading-relaxed line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section className="mfb-section border-t border-white/5" data-testid="faq-section">
        <div className="mfb-container grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline mb-3">Frequently Asked</div>
            <h2 className="font-[Outfit] text-4xl font-light tracking-tight text-white">Everything you wanted to know.</h2>
            <p className="mt-4 text-slate-400 text-sm">Still curious? <Link to="/contact" className="text-sky-400 hover:text-sky-300">Talk to an advisor →</Link></p>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem key={f.id} value={`item-${i}`} className="border border-white/10 rounded-xl px-5 bg-[#0B1221]" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-white hover:text-sky-300 text-sm font-medium hover:no-underline">
                    {f.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400 text-sm leading-relaxed">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="mfb-section">
        <div className="mfb-container">
          <div className="relative rounded-3xl overflow-hidden border border-white/10 glass p-10 sm:p-16">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.18),transparent_50%),radial-gradient(circle_at_bottom_right,rgba(212,175,55,0.16),transparent_55%)]" />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <ShieldCheck className="w-8 h-8 text-[#D4AF37]" />
                <h2 className="mt-4 font-[Outfit] text-3xl sm:text-5xl font-light tracking-tight text-white">
                  Open your premium account <span className="gradient-text-gold">in 5 minutes</span>.
                </h2>
                <p className="mt-4 text-slate-300 max-w-lg text-sm leading-relaxed">
                  Fully digital onboarding. Zero account opening fee. Talk to a dedicated wealth advisor.
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    data-testid="cta-open-account"
                    className="btn-primary"
                    onClick={() => trackEvent("open_account_click", { source: "cta_bottom" })}
                  >
                    Open Account <ArrowRight className="w-4 h-4" />
                  </Link>
                  <CalendlyButton label="Book Consultation" source="cta_bottom" testId="cta-contact-advisor" />
                </div>
              </div>
              <ul className="space-y-3 text-sm text-slate-300">
                {[
                  "Zero account opening fee, zero AMC",
                  "256-bit encryption + multi-factor auth",
                  "Dedicated wealth advisor on chat & call",
                  "Access to AI portfolios from day one",
                ].map((x) => (
                  <li key={x} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-emerald-400" /> {x}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
